import { camelCase, get, isNil } from 'lodash';
import {
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO
} from 'utils/deliverables';
import { isInstagramUrl, removeFinalSlash, stripQuery } from 'utils/url';
import { isMetricsStillFetching } from 'utils/helper/profile';

import type {
  Channel,
  ChannelMetric,
  ChannelShort,
  Channels,
  Deliverable,
  DeliverableAttachment
} from 'types';

export const CHANNEL_TYPE_INSTAGRAM = 'instagram';
export const CHANNEL_TYPE_YOUTUBE = 'youtube';
export const CHANNEL_TYPE_BLOG = 'blog';
export const CHANNEL_TYPE_TWITTER = 'twitter';
export const CHANNEL_TYPE_SNAPCHAT = 'snapchat';
export const CHANNEL_TYPE_FACEBOOK = 'facebook';
export const CHANNEL_TYPE_PINTEREST = 'pinterest';
export const CHANNEL_TYPE_TUMBLR = 'tumblr';
export const CHANNEL_TYPE_TIKTOK = 'tiktok';

export const EXTRA_CHANNELS = [
  CHANNEL_TYPE_BLOG,
  CHANNEL_TYPE_PINTEREST,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_FACEBOOK,
  CHANNEL_TYPE_SNAPCHAT,
  CHANNEL_TYPE_TWITTER
];

export const CHANNELS_WITH_METRICS = [
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
];

export const MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL = 1000;

export const isChannelHasEnoughFollowers = (channel: any): boolean => {
  if (!channel || !channel.metrics) {
    return false;
  }

  if (channel.metrics instanceof Array) {
    const followers = channel.metrics.find(
      value => value.name === 'followers' || value.name === 'num_followers'
    );

    return (followers?.value ?? 0) >= MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL;
  }

  return (channel.metrics?.followers ?? 0) >= MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL;
};

export function getExtraChannelsFromChannelsObject(channels: Channels): Array<Channel> {
  const influencerChannels = EXTRA_CHANNELS.filter(type => channels[type] && channels[type].url);

  const primaryChannelsWithoutEnoughFollowers = CHANNELS_WITH_METRICS
    .filter(type => channels[type] && channels[type].url)
    .reduce<Array<Channel>>((result, type) => {
      const channelInfo = channels[type];
      if (channelInfo && (channelInfo.metrics?.followers < 1000 || isMetricsStillFetching(channelInfo.metrics))) {
        result.push(channelInfo);
      }
      return result;
    }, []);

  return influencerChannels.reduce<Array<Channel>>((result, type) => {
    const channelInfo = channels[type];
    const alreadyHasChannelAdded = result.some(channel => channel.type === type);

    if (channelInfo && !alreadyHasChannelAdded) {
      result.push(channelInfo);
    }
    return result;
  }, primaryChannelsWithoutEnoughFollowers);
}

export function getYoutubeVideoThumbnail(videoUrl: string): string {
  const regExp = /^(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/;
  const match = videoUrl.match(regExp);

  if (isNil(match) || match.length !== 2) {
    return '';
  }

  return `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg`;
}

export function getYoutubeChannelName(channel: Channel): string {
  if (!channel) {
    return 'Youtube Channel';
  }

  if (channel.userName) {
    return channel.userName;
  }

  return 'Youtube Channel';
}

export function getInstagramChannelName(channel: Channel): string {
  if (!channel) {
    return 'Instagram Channel';
  }

  if (channel.userName) {
    return `@${channel.userName}`;
  }

  return 'Instagram Channel';
}

export function getYoutubeEmbedUrl(videoUrl: string): string {
  const regExp = /^(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/;
  const match = videoUrl.match(regExp);

  if (isNil(match) || match.length !== 2) {
    return '';
  }

  return `https://www.youtube-nocookie.com/embed/${match[1]}`;
}

export function getVisibleAttachmentsFromDeliverable(deliverable: Deliverable): Array<DeliverableAttachment> {
  const post = deliverable.post;
  const attachments = deliverable.postAttachments || [];
  const deliverableType = deliverable.deliverableType;
  let visibleAttachments: Array<DeliverableAttachment> = [];

  switch (deliverableType) {
    case DELIVERABLE_TYPE_YOUTUBE_VIDEO:
      // generate a fake attachment from Youtube video
      if (post) {
        visibleAttachments.push({
          id: post.id,
          url: getYoutubeVideoThumbnail(post.url),
          externalUrl: post.url,
          assetType: 'image',
          meta: {
            type: DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
          }
        });
      }
      break;
    case DELIVERABLE_TYPE_INSTAGRAM_POST:
      // generate a fake attachment from Instagram post
      if (post) {
        visibleAttachments.push({
          id: post.id,
          url: post.url,
          externalUrl: post.url,
          assetType: 'image',
          meta: {
            type: DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
          }
        });
      }
      break;
    case DELIVERABLE_TYPE_INSTAGRAM_REEL:
      // generate a fake attachment from Instagram reel
      if (post) {
        visibleAttachments.push({
          id: post.id,
          url: post.url,
          externalUrl: post.url,
          assetType: 'image',
          meta: {
            type: DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
          }
        });
      }
      break;
    case DELIVERABLE_TYPE_INSTAGRAM_STORY:
      visibleAttachments = (attachments || []).filter(a => a.meta && a.meta.type === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES);
      break;
    case DELIVERABLE_TYPE_TIKTOK_VIDEO:
      // generate a fake attachment from TikTok video
      if (post) {
        visibleAttachments.push({
          id: post.id,
          url: post.thumbnailUrl,
          externalUrl: post.url,
          assetType: 'image',
          meta: {
            type: DELIVERABLE_ATTACHMENT_TYPE_HIGHRES
          }
        });
      }
      break;
    default:
      break;
  }

  return visibleAttachments;
}

export function getUserNameFromChannel(channel: Channel | ChannelMetric | ChannelShort): string {
  if (!channel.username && (channel.type === 'instagram' || channel.type === CHANNEL_TYPE_TIKTOK)) {
    // This is necessary for the new influencer signup. Eventually it should be implemented on API side
    const parts = removeFinalSlash(stripQuery(channel.url)).split('/');
    return parts.length > 1 ? parts[parts.length - 1].replace('@', '') : '';
  }

  return channel.username || get(channel, 'userName') || '';
}

export function getChannelFromInstagramUrl(value: string): string {
  if (!isInstagramUrl(value)) {
    return value;
  }

  const result = value.match(/[^/]+(?=\/$|$)/) || [''];

  return result[0];
}

export function isInstagramChannel(channel: string): boolean {
  return channel === CHANNEL_TYPE_INSTAGRAM;
}

export function isYoutubeChannel(channel: string): boolean {
  return channel === CHANNEL_TYPE_YOUTUBE;
}

export function isBlogChannel(channel: string): boolean {
  return channel === CHANNEL_TYPE_BLOG;
}

export function getChannelUserNameWithFallback(channel: Channel): string {
  return channel.username || channel.userName || channel.url.replace(/http(s)?:\/\/(www\.)?/, '');
}

export function isTiktokChannel(channel: string): boolean {
  return channel === CHANNEL_TYPE_TIKTOK;
}

export function getTiktokEmbedUrl(videoUrl: string): string {
  const regExp = /^((https?):\/\/)?(www.)?tiktok\.com(\/@?.*?)?\/video\/([0-9]+)\/?/;
  const match = videoUrl.match(regExp);

  if (isNil(match) || match.length !== 6) {
    return '';
  }

  // URL can be extracted from IFrame using next manual https://developers.tiktok.com/doc/Embed
  return `https://www.tiktok.com/embed/v2/${match[5]}`;
}

export const convertChannelArrayToChannelsObject = (channels: Array<Channel>): Channels => channels.reduce(
  (acc, value) => {
    const { type, metrics, ...rest } = value;

    const metricsArray = (Array.isArray(metrics) ? metrics : []);
    const channel = (metricsArray || []).reduce(
      (channelAcc, metric) => {
        const result = { ...channelAcc };
        result[camelCase(metric.name)] = metric.value;

        return result;
      },
      rest
    );

    acc[type] = {
      ...channel,
      metrics,
      type
    };

    return acc;
  },
  {}
);
